import { z } from 'zod';

import type { QueryFilter } from '@/types';

/**
 * Zod schema for QueryFilter with recursive support for OR/AND operators
 * Matches GraphQuery type definition from @/types (lines 264-277)
 *
 * Supports nested filters in the value field for OR/AND operators
 * using z.lazy() for recursive references
 */
const QueryFilterSchema: z.ZodType<QueryFilter> = z.lazy(() =>
  z
    .object({
      field: z.string(),
      operator: z.enum([
        '=',
        '<>',
        '<',
        '<=',
        '>',
        '>=',
        'LIKE',
        'CONTAINS',
        'STARTS WITH',
        'ENDS WITH',
        'IN',
        'IS NULL',
        'IS NOT NULL',
        'OR',
        'AND',
        'ANY_STARTS_WITH',
        'ANY_IN',
      ]),
      value: z.union([
        z.string(),
        z.number(),
        z.boolean(),
        z.array(z.union([z.string(), z.number(), z.boolean()])),
        z.array(z.array(z.union([z.string(), z.number(), z.boolean()]))),
        z.array(QueryFilterSchema), // Recursive: supports nested filters for OR/AND
      ]),
      not: z.boolean().optional(),
      reverseOperands: z.boolean().optional(),
      alias: z.string().optional(),
    })
    .strict()
);

/**
 * Zod schema for NodeDetails relationships
 * Matches NodeDetails.relationships type (lines 294-303)
 */
const RelationshipSchema: z.ZodTypeAny = z.lazy(() =>
  z
    .object({
      label: z.union([z.string(), z.array(z.string())]),
      length: z.number().optional(),
      source: NodeDetailsSchema.optional(),
      target: NodeDetailsSchema.optional(),
      optional: z.boolean().optional(),
      select_one: z.boolean().optional(),
      not_exists: z.boolean().optional(),
      self_edges: z.enum(['forbid', 'allow', 'require']).optional(),
      alias: z.string().optional(),
    })
    .strict()
);

/**
 * Zod schema for NodeDetails
 * Matches NodeDetails interface (lines 290-303)
 */
const NodeDetailsSchema: z.ZodTypeAny = z.lazy(() =>
  z
    .object({
      labels: z.array(z.string()).optional(),
      sorted: z.boolean().optional(),
      filters: z.array(QueryFilterSchema).optional(),
      relationships: z.array(RelationshipSchema).optional(),
      self_edges: z.enum(['forbid', 'allow', 'require']).optional(),
      alias: z.string().optional(),
    })
    .strict()
);

/**
 * Zod schema for GraphQuery
 * Matches GraphQuery interface from @/types (lines 279-288)
 */
export const GraphQuerySchema = z
  .object({
    node: NodeDetailsSchema,
    page: z.number().optional(),
    limit: z.number().optional(),
    orderBy: z.string().optional(),
    descending: z.boolean().optional(),
    filters: z.array(QueryFilterSchema).optional(),
    shortest: z.number().optional(),
    usernames: z.array(z.string()).optional(),
  })
  .strict();

export type GraphQueryValidation = z.infer<typeof GraphQuerySchema>;
